import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { fetchPageBySlug } from '@/lib/api';
import type { Page } from '@/lib/types';

export default function CustomPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    fetchPageBySlug(slug)
      .then((p) => setPage(p))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load page'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    document.title = page ? `${page.title} — TGMCI` : 'TGMCI';
    window.scrollTo(0, 0);
  }, [page]);

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-40 pb-20">
        <div className="max-w-[820px] mx-auto px-4 md:px-8">
          {loading ? (
            <p className="text-center text-foreground-400 py-16">
              <i className="ri-loader-4-line animate-spin inline-block" aria-hidden="true" /> Loading...
            </p>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-red-600 text-sm mb-3">Could not load this page.</p>
              <Link to="/" className="btn-primary">Back to Home</Link>
            </div>
          ) : !page ? (
            <div className="text-center py-16">
              <i className="ri-file-unknow-line text-4xl text-foreground-300" aria-hidden="true" />
              <h1 className="font-heading text-2xl font-bold text-foreground-800 mt-4">Page not found</h1>
              <p className="text-foreground-500 text-sm mt-2">The page you&apos;re looking for doesn&apos;t exist.</p>
              <Link to="/" className="btn-primary mt-6">Back to Home</Link>
            </div>
          ) : (
            <article>
              <p className="text-accent-600 font-label text-sm tracking-widest uppercase font-semibold mb-3">TGMCI</p>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-4">{page.title}</h1>
              <div className="w-12 h-1 bg-accent-500 mb-8" />
              <div className="text-foreground-700 leading-relaxed whitespace-pre-wrap text-base">
                {page.content}
              </div>
            </article>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}